import React, { useState, useRef, useCallback } from 'react';
import { Card, Button, Spinner, Form, Modal, Table, Badge } from 'react-bootstrap';
import { Search, Database, Brain, ChevronDown, ChevronUp, Eye, EyeOff, GripVertical, X } from 'lucide-react';
// Generated by Copilot — Migrated from Userfront to Clerk
import useAuthHeaders from '../hooks/useAuthHeaders';
import './Facturin.css';

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

const SQL_EXAMPLES = [
  'Facturas por RFC emisor con totales',
  'Top 5 emisores por monto total',
  'Facturas de este mes',
  'Facturas con IVA mayor a 1000',
  'Resumen de pagos por método'
];

const SEMANTIC_EXAMPLES = [
  'Servicios financieros bancarios',
  'Servicios de internet y telecomunicaciones',
  'Compra de materiales de oficina',
  'Servicios profesionales de consultoría',
  'Arrendamiento de inmuebles'
];

const formatColumnName = (name) => {
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const isNumericValue = (val) => {
  if (val === null || val === undefined || val === '') return false;
  return !isNaN(Number(val));
};

const formatCellValue = (val, colName) => {
  if (val === null || val === undefined) return <span className="text-muted">—</span>;
  if (typeof val === 'object') return JSON.stringify(val);

  const lowerCol = (colName || '').toLowerCase();
  if (lowerCol === 'similarity') {
    const pct = (Number(val) * 100).toFixed(1);
    return (
      <div className="similarity-cell">
        <div className="similarity-bar" style={{ width: `${pct}%` }} />
        <span className="similarity-text">{pct}%</span>
      </div>
    );
  }

  if (isNumericValue(val) && (lowerCol.includes('total') || lowerCol.includes('subtotal') || lowerCol.includes('monto') || lowerCol.includes('importe') || lowerCol.includes('descuento'))) {
    return <span className="number-value">${Number(val).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
  }

  if (isNumericValue(val) && !lowerCol.includes('rfc') && !lowerCol.includes('uuid') && !lowerCol.includes('folio') && !lowerCol.includes('serie')) {
    return <span className="number-value">{Number(val).toLocaleString('es-MX')}</span>;
  }

  return String(val);
};

const Facturin = () => {
  const [mode, setMode] = useState('sql');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [generatedSql, setGeneratedSql] = useState('');
  const [columnOrder, setColumnOrder] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const { getToken } = useAuthHeaders();

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError('');
    setResults(null);
    setGeneratedSql('');
    setStatusText(mode === 'sql' ? 'Generando consulta con IA...' : 'Buscando por similitud semántica...');

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL_V2}/Busqueda/query`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mode: mode,
          query: query,
          matchThreshold: 0.5,
          matchCount: 20
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Error ${response.status}`);
      }

      const data = await response.json();

      if (data.generatedSql) {
        setGeneratedSql(data.generatedSql);
      }

      const rows = data.results || [];
      setResults({ rows, count: data.count, mode: data.mode, query: data.query });

      // Set column order from first row keys
      if (rows.length > 0) {
        const cols = Object.keys(rows[0]);
        setColumnOrder(cols);
        setHiddenColumns(new Set());
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setStatusText('');
    }
  }, [query, mode]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const toggleColumn = (col) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(col)) {
        next.delete(col);
      } else {
        next.add(col);
      }
      return next;
    });
  };

  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const items = [...columnOrder];
    const draggedItem = items[dragItem.current];
    items.splice(dragItem.current, 1);
    items.splice(dragOverItem.current, 0, draggedItem);
    setColumnOrder(items);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const visibleColumns = columnOrder.filter(col => !hiddenColumns.has(col));

  const openPreview = (row) => {
    setPreviewData(row);
    setShowPreview(true);
  };

  const tipoComprobanteLabel = (tipo) => {
    const map = { I: 'Ingreso', E: 'Egreso', P: 'Pago', N: 'Nómina', T: 'Traslado' };
    return map[tipo] || tipo;
  };

  const formaPagoLabel = (code) => {
    const map = { '01': 'Efectivo', '02': 'Cheque', '03': 'Transferencia', '04': 'Tarjeta de crédito', '99': 'Por definir' };
    return map[code] || code;
  };

  const metodoPagoLabel = (code) => {
    const map = { PUE: 'Pago en una sola exhibición', PPD: 'Pago en parcialidades o diferido' };
    return map[code] || code;
  };

  return (
    <div className="facturin-container">
      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">Facturin</h2>
          <p className="page-subtitle text-muted">Pregunta lo que quieras sobre tus facturas CFDI</p>
        </div>
      </div>

      {/* Query Card */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-4">
          {/* Mode Toggle */}
          <div className="mode-toggle mb-3">
            <Button
              variant={mode === 'sql' ? 'primary' : 'outline-secondary'}
              className={`mode-btn ${mode === 'sql' ? 'active' : ''}`}
              onClick={() => setMode('sql')}
            >
              <Database size={16} className="me-2" />
              SQL Analítico
            </Button>
            <Button
              variant={mode === 'semantic' ? 'primary' : 'outline-secondary'}
              className={`mode-btn ${mode === 'semantic' ? 'active' : ''}`}
              onClick={() => setMode('semantic')}
            >
              <Brain size={16} className="me-2" />
              Semántico
            </Button>
          </div>

          {/* Search Input */}
          <div className="search-row">
            <Form.Control
              type="text"
              className="search-input"
              placeholder={mode === 'sql'
                ? 'Escribe tu pregunta en lenguaje natural...'
                : 'Describe qué tipo de conceptos buscas...'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <Button
              className="btn-gradient search-btn"
              onClick={handleSearch}
              disabled={loading || !query.trim()}
            >
              {loading ? <Spinner size="sm" animation="border" /> : <Search size={18} />}
            </Button>
          </div>

          {/* Example Chips */}
          <div className="example-chips mt-3">
            {(mode === 'sql' ? SQL_EXAMPLES : SEMANTIC_EXAMPLES).map((ex, i) => (
              <span
                key={i}
                className="example-chip"
                onClick={() => setQuery(ex)}
              >
                {ex}
              </span>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Status */}
      {loading && (
        <div className="status-box mb-4">
          <Spinner animation="border" size="sm" variant="primary" className="me-2" />
          <span>{statusText}</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="error-box mb-4">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Generated SQL */}
      {generatedSql && (
        <Card className="shadow-sm border-0 mb-4 sql-card">
          <Card.Body className="p-3">
            <div className="sql-label">SQL Generado</div>
            <pre className="sql-code">{generatedSql}</pre>
          </Card.Body>
        </Card>
      )}

      {/* Results */}
      {results && results.rows.length > 0 && (
        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="p-4">
            {/* Results Header */}
            <div className="results-header mb-3">
              <span className="results-count">
                <strong className="text-primary">{results.count}</strong> resultados encontrados
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowColumnManager(!showColumnManager)}
              >
                {showColumnManager ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                <span className="ms-1">Columnas</span>
              </Button>
            </div>

            {/* Column Manager */}
            {showColumnManager && (
              <div className="column-manager mb-3">
                {columnOrder.map((col, index) => (
                  <div
                    key={col}
                    className={`column-chip ${hiddenColumns.has(col) ? 'hidden-col' : 'visible-col'}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <GripVertical size={12} className="grip-icon" />
                    <span>{formatColumnName(col)}</span>
                    <span
                      className="toggle-vis"
                      onClick={(e) => { e.stopPropagation(); toggleColumn(col); }}
                    >
                      {hiddenColumns.has(col) ? <EyeOff size={14} /> : <Eye size={14} />}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Results Table */}
            <div className="table-wrap">
              <Table className="modern-table" hover>
                <thead>
                  <tr>
                    {results.rows[0]?.uuid !== undefined && <th className="th-action">CFDI</th>}
                    {visibleColumns.map(col => (
                      <th key={col}>{formatColumnName(col)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.rows.map((row, i) => (
                    <tr key={i}>
                      {row.uuid !== undefined && (
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="preview-btn"
                            onClick={() => openPreview(row)}
                          >
                            Ver
                          </Button>
                        </td>
                      )}
                      {visibleColumns.map(col => (
                        <td key={col} className={isNumericValue(row[col]) ? 'text-end' : ''}>
                          {formatCellValue(row[col], col)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* No Results */}
      {results && results.rows.length === 0 && (
        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="p-4 text-center text-muted">
            No se encontraron resultados para tu búsqueda.
          </Card.Body>
        </Card>
      )}

      {/* CFDI Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="lg"
        centered
        className="cfdi-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Comprobante Fiscal Digital
            {previewData?.tipoComprobante && (
              <Badge
                bg={previewData.tipoComprobante === 'I' ? 'success' : previewData.tipoComprobante === 'E' ? 'warning' : 'info'}
                className="ms-2"
              >
                {tipoComprobanteLabel(previewData.tipoComprobante)}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewData && (
            <div className="cfdi-preview">
              {/* UUID & Date */}
              <div className="cfdi-header-info mb-4">
                <div className="cfdi-uuid">
                  <small className="text-muted">UUID</small>
                  <div className="fw-bold font-monospace">{previewData.uuid}</div>
                </div>
                {previewData.fecha && (
                  <div className="cfdi-date">
                    <small className="text-muted">Fecha</small>
                    <div>{new Date(previewData.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                  </div>
                )}
                {previewData.folio && (
                  <div>
                    <small className="text-muted">Folio</small>
                    <div className="fw-bold">{previewData.serie ? `${previewData.serie}-` : ''}{previewData.folio}</div>
                  </div>
                )}
              </div>

              {/* Emisor / Receptor */}
              <div className="cfdi-parties mb-4">
                <Card className="party-card">
                  <Card.Body>
                    <h6 className="text-primary mb-2">Emisor</h6>
                    <div className="fw-bold">{previewData.emisorNombre || previewData.emisor_nombre || '—'}</div>
                    <div className="font-monospace text-muted">{previewData.emisorRfc || previewData.emisor_rfc || '—'}</div>
                  </Card.Body>
                </Card>
                <Card className="party-card">
                  <Card.Body>
                    <h6 className="text-primary mb-2">Receptor</h6>
                    <div className="fw-bold">{previewData.receptorNombre || previewData.receptor_nombre || '—'}</div>
                    <div className="font-monospace text-muted">{previewData.receptorRfc || previewData.receptor_rfc || '—'}</div>
                  </Card.Body>
                </Card>
              </div>

              {/* Payment Info */}
              <div className="cfdi-payment mb-4">
                <div className="payment-item">
                  <small className="text-muted">Forma de Pago</small>
                  <div>{formaPagoLabel(previewData.formaPago || previewData.forma_pago)}</div>
                </div>
                <div className="payment-item">
                  <small className="text-muted">Método de Pago</small>
                  <div>{metodoPagoLabel(previewData.metodoPago || previewData.metodo_pago)}</div>
                </div>
                <div className="payment-item">
                  <small className="text-muted">Moneda</small>
                  <div>{previewData.moneda || '—'}</div>
                </div>
              </div>

              {/* Conceptos */}
              {previewData.conceptosDescripcion && (
                <div className="cfdi-conceptos mb-4">
                  <h6 className="text-primary mb-2">Conceptos</h6>
                  <div className="conceptos-text">{previewData.conceptosDescripcion || previewData.conceptos_descripcion}</div>
                </div>
              )}

              {/* Totals */}
              <div className="cfdi-totals">
                {previewData.subtotal !== undefined && (
                  <div className="total-row">
                    <span>Subtotal</span>
                    <span className="font-monospace">${Number(previewData.subtotal).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="total-row total-main">
                  <span>Total</span>
                  <span className="font-monospace fw-bold">${Number(previewData.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* Similarity (if semantic search) */}
              {previewData.similarity !== undefined && (
                <div className="mt-3">
                  <small className="text-muted">Similitud</small>
                  <div className="similarity-cell mt-1">
                    <div className="similarity-bar" style={{ width: `${(previewData.similarity * 100).toFixed(0)}%` }} />
                    <span className="similarity-text">{(previewData.similarity * 100).toFixed(1)}%</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Facturin;
